import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames/bind';
import styles from './index.pcss';
import { getStoreName, gtmEventPush, gtmEventPushPLP} from "../../../modules/ga/GTMdatalayer";
import { getHostName } from '../../../utils/sisUtils';
import isNode from '../../../modules/ajio-util/isNode';
import Icon from './../../atoms/Icon';
import { checkMyJioRedirection } from '../../../refresh/utils/myJioUtils';
import { getFeatureVariant } from '../../../refresh/organism/ABFeature/Feature';
import { getReleaseGA4Flag, getMasterGA4Flag, getMasterGA3Flag } from "../../../refresh/selectors/featureFlagsSelector"


export interface ToastProps {
  message: string | HTMLElement;
  position?: 'top' | 'bottom';
  visibility: boolean;
  time?: number;
  onClose?: () => void;
  customClass?: string;
  selectedSize?: string;
  customHeadingClass?: string;
  heading?: string;
  customTextClass?: string;
  customContainerClass?: string;
  displayCloseIcon?: boolean;
}

const cx = classNames.bind(styles);

const Toast = (props: ToastProps): JSX.Element => {
  const {
    message,
    position,
    visibility,
    onClose,
    time,
    customClass,
    selectedSize = '',
    heading = '',
    customHeadingClass,
    customTextClass,
    customContainerClass,
    displayCloseIcon = false
  } = props;
  const [visible, setVisible] = useState(() => {
    return visibility;
  });
  const isMyJioRedirection = checkMyJioRedirection();
  const [liveText, setLiveText] = useState('');
  const contentRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!isNode()) {
      const masterGA3 = getMasterGA3Flag() === 'ON';
      const masterGA4 = getMasterGA4Flag() === 'ON';
      const releaseGA4 = getReleaseGA4Flag() === 'ON';
      const isGA4Feature = getFeatureVariant('isGA4Feature');
      let is_GA4 = false;

      const storeName = getStoreName()
      if (isGA4Feature === '1' && releaseGA4 && masterGA4 && !masterGA3 && storeName === 'ajio') {
        is_GA4 = true;
      };
      const { location: { origin = '' } } = window;
      if(is_GA4){
        if (selectedSize) {
          gtmEventPushPLP({
            event: 'plp_add_to_whishlist',
            event_category: 'plp_interaction',
            event_action: 'add to wishlist',
            event_label: message,
            store_type: getHostName(origin) || 'AJIO',
            product_size: selectedSize,
            page_type: window.pageType || '',
            is_GA4
          });
        } else {
          gtmEventPushPLP({
            event: 'plp_navigation_interaction',
            event_category: 'user interactions',
            event_action: 'user_communication',
            event_label: message,
            store_type: getHostName(origin) || 'AJIO',
            is_GA4
          });
        }
      } else if (selectedSize) {
        gtmEventPush({
          event: 'user_communication',
          event_category: 'user interactions',
          event_action: 'user_communication',
          event_label: message,
          store_type: getHostName(origin) || 'AJIO',
          product_size: selectedSize
        });
      } else {
        gtmEventPush({
          event: 'user_communication',
          event_category: 'user interactions',
          event_action: 'user_communication',
          event_label: message,
          store_type: getHostName(origin) || 'AJIO'
        });
      }
    }
  }, []);
  useEffect(() => {
    let animate = document.getElementById('toast');
    if (visible) {
      if(animate)
        animate.style.animationDelay = `${time ? time : 0 / 1000 - 1 + 0.7}s`;
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
        setVisible(false);
      }, time);
    }
  }, [visible, time, onClose]);

  useEffect(() => {
    if (!visible) return;
    // Prefer explicit string; else read rendered text from the content span
    const body = typeof message === 'string' ? message : contentRef.current?.innerText || '';
    const composed = (heading ? `${heading}. ` : '') + (body || '');
    // Clear → next frame → set to force re-announce across SRs
    setLiveText('');
    const id = requestAnimationFrame(() => setLiveText(composed));
    return () => cancelAnimationFrame(id);
  }, [visible, message, heading]);

  const classes = cx(
    {
      [`${position}`]: true,
      [`show-${position}`]: visible,
      'is-my-jio-integration': isMyJioRedirection
    },
    customContainerClass
  );
  const toastClass = cx('toast', customClass);
  const textClass = cx('text-content', customTextClass);
  const headingClass = cx(customHeadingClass);
  const iconContainer = cx('icon-container');
  const iconStyle = {
    fontSize: '12px',
    color: 'white'
  };

  return (
    <div className={toastClass}>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        tabIndex={-1}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          margin: -1,
          border: 0,
          padding: 0,
          clip: 'rect(0 0 0 0)',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }}
      >
        {liveText}
      </div>
      <div id="toast" className={classes}>
        {displayCloseIcon && (
          <div className={iconContainer}>
            <Icon icon="icon-Close" styles={iconStyle} onClick={onClose} ariaLabel='Close' />
          </div>
        )}
        {heading && <p className={headingClass}>{heading}</p>}
        <span className={textClass} ref={contentRef}>
          {message}
        </span>
      </div>
    </div>
  );
};

Toast.defaultProps = {
  position: 'bottom',
  time: 3000
};

export default Toast;
